import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import RestoreIcon from '@mui/icons-material/Restore';
import { TableCell, TableRow } from '@mui/material';

import { reverseFormatDate } from '../../../utils/dateFunctions';
import { clearKeys } from '../../../utils/objectUtils';
import SingleSelect from '../../formElements/SingleSelect';

import { validate } from './validateSelfEmployedCourseStatus';

import table from '../../../commonStyles/tables.module.css';

const SelfEmployedRow = ({
    row,
    isDisabled,
    isCSVDisabled,
    onUpdateApprovalStatus,
    isAuditable,
    onSearchAuditItems
}) => {
    // LOCAL STATE
    const initialErrorState = { approvalStatusId: { error: false, message: '' } };
    const [courseModuleStatus, setCourseModuleStatus] = useState({});
    const [courseModuleStatusesTrimmed, setCourseModuleStatusesTrimmed] = useState([]);
    const [errors, setErrors] = useState(initialErrorState);
    const [keys, setKeys] = useState({
        approvalStatusId: '0'
    });

    // STORE STATE
    const courseModuleStatuses = useSelector(
        (state) => state.entities.directusService.selfEmployedManagementCourseModuleStatuses
    );
    const selfEmployedCourseModules = useSelector(
        (state) => state.entities.courseService.selfEmployedCourseModules
    );

    // USEEFFECTS
    useEffect(() => {
        setKeys(clearKeys(keys));
    }, []);

    useEffect(() => {
        if (!courseModuleStatuses?.length || !selfEmployedCourseModules?.length) return;

        let approvalId;
        if (courseModuleStatus?.id) approvalId = courseModuleStatus.id;
        else approvalId = row.approvalStatusId;

        const courseModuleStatusFound = courseModuleStatuses.find((el) => el.id === approvalId);

        if (!courseModuleStatusFound) return;

        setCourseModuleStatus(courseModuleStatusFound);

        switch (courseModuleStatusFound?.name.toLowerCase()) {
            case 'logged':
                setCourseModuleStatusesTrimmed(
                    courseModuleStatuses.filter((el) => el.name.toLowerCase() !== 'paid')
                );
                break;
            case 'approved':
                isCSVDisabled
                    ? setCourseModuleStatusesTrimmed(
                          courseModuleStatuses.filter(
                              (el) => el.name.toLowerCase() !== 'paid'.toLowerCase()
                          )
                      )
                    : setCourseModuleStatusesTrimmed(courseModuleStatuses);
                break;
            case 'rejected':
                setCourseModuleStatusesTrimmed(
                    courseModuleStatuses.filter(
                        (el) =>
                            el.name.toLowerCase() !== 'paid'.toLowerCase() &&
                            el.name.toLowerCase() !== 'approved'.toLowerCase()
                    )
                );
                break;
            case 'paid':
                setCourseModuleStatusesTrimmed(
                    courseModuleStatuses.filter(
                        (el) => el.name.toLowerCase() !== 'logged'.toLowerCase()
                    )
                );
        }
    }, [selfEmployedCourseModules, courseModuleStatuses]);

    // HELPER FNS
    const clearError = (key) => {
        setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    // EVENT HANDLERS
    const onApprovalStatusIdChange = (id) => {
        clearError('approvalStatusId');

        if (courseModuleStatus?.id && courseModuleStatus.id !== id) {
            const newErrors = validate(id);

            if (Object.keys(newErrors).length > 0) {
                setErrors(newErrors);
                return;
            }

            onUpdateApprovalStatus(row.id, id);
            const selected = courseModuleStatuses.find((el) => el.id === id);
            selected && setCourseModuleStatus(selected);
        }
    };

    return (
        <TableRow hover className={`${table.basicRowStripes} ${table.rowHeight}`}>
            <TableCell>{row.courseInstanceCode}</TableCell>
            <TableCell>{row.moduleName}</TableCell>
            <TableCell>{row.moduleDate ? reverseFormatDate(row.moduleDate) : ' - '}</TableCell>
            <TableCell>{row.moduleTime}</TableCell>
            <TableCell>{row.trainerName}</TableCell>
            <TableCell>
                <div className={table.inputAuditWrapper}>
                    <SingleSelect
                        id={'approvalStatusId'}
                        key={keys.approvalStatusId}
                        disabled={
                            isDisabled ||
                            (isCSVDisabled && row.approvalStatus.toLowerCase() === 'paid')
                        }
                        placeholder="Approval status..."
                        mandatory={true}
                        customClass="tableRowSelector"
                        menuItems={courseModuleStatusesTrimmed}
                        selectedId={courseModuleStatus?.id || ''}
                        selected={
                            courseModuleStatuses.find((el) => el.id === courseModuleStatus?.id) ||
                            ''
                        }
                        error={errors.approvalStatusId}
                        onChange={(chosenId) => onApprovalStatusIdChange(chosenId)}
                    />
                    {isAuditable && (
                        <span className={table.inputAuditIcon}>
                            <RestoreIcon
                                onClick={() => onSearchAuditItems(row.id, 'Approval Status')}
                            />
                        </span>
                    )}
                </div>
            </TableCell>
        </TableRow>
    );
};

SelfEmployedRow.propTypes = {
    row: PropTypes.object,
    isDisabled: PropTypes.bool,
    isCSVDisabled: PropTypes.bool,
    onUpdateApprovalStatus: PropTypes.func,
    isAuditable: PropTypes.bool,
    onSearchAuditItems: PropTypes.func
};

export default SelfEmployedRow;
